import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking");
  const polls = useRef(0);

  const isCancel = location.pathname.includes("cancel");

  useEffect(() => {
    if (isCancel) { setState("cancelled"); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let timer;
    const poll = async () => {
      if (polls.current >= 12) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          await checkAuth();
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(r.data.status)) { setState("error"); return; }
      } catch (e) { console.error("Payment status check failed:", e); }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [location, isCancel, checkAuth]);

  const content = {
    checking: { icon: <Loader2 className="w-12 h-12 text-primary animate-spin" />, title: "Confirming your payment…", desc: "Hang tight, this only takes a moment." },
    success: { icon: <CheckCircle2 className="w-12 h-12 text-emerald-400" />, title: "You're subscribed!", desc: "Your Monthly Pro access is active. Time to move some freight." },
    cancelled: { icon: <XCircle className="w-12 h-12 text-amber-400" />, title: "Checkout cancelled", desc: "No charge was made. You can subscribe anytime." },
    error: { icon: <XCircle className="w-12 h-12 text-destructive" />, title: "Something went wrong", desc: "We couldn't confirm the payment. Please try again." },
    timeout: { icon: <XCircle className="w-12 h-12 text-amber-400" />, title: "Still processing", desc: "Payment is taking longer than usual. Check your subscription status shortly." },
  }[state];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div data-testid="payment-result" className="max-w-md w-full text-center rounded-2xl border border-border bg-card/60 p-10">
        <div className="flex justify-center mb-5">{content.icon}</div>
        <h1 className="font-heading font-bold text-2xl mb-2">{content.title}</h1>
        <p className="text-muted-foreground mb-7">{content.desc}</p>
        <Button data-testid="payment-continue-btn" onClick={() => navigate(state === "success" ? "/app" : "/app/subscription")}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
          {state === "success" ? "Go to Load Board" : "Back to Subscription"}
        </Button>
      </div>
    </div>
  );
}
